// The barrier-resolution chain: what happens when an object's properties cross
// the threshold it was watching for.
//
// Moved out of GameScene (2026-07-30, sim extraction step 3a). The logic is
// unchanged; what changed is that the Phaser half is no longer interleaved with
// it. Where these functions used to reach for a sprite and tween it, they now
// append a SimEvent (see events.ts) and let the caller decide whether anything is
// drawn. All the subtle behavior that earlier sessions bled for -- the
// `transformed` flag being the source of truth rather than the sprite's texture,
// the hadSecondStage guard against double-toggling -- is preserved verbatim, with
// its original reasoning attached. (The 'below'-crossing property zeroing that
// used to live here, closing the room6 Entropy exploit, was reverted 2026-08-06
// in favor of a conservative fix -- see 12_Mechanics_Entropy.md.)
import { ObjectDef, RoomDef } from './types';
import { SimEvents } from './events';
import { meltRulesSatisfied } from './queries';
import { checkHeatStateTransition, checkWaterFreeze } from './heatState';
import { DEFAULT_PHYSICS, PhysicsConfig } from './physics';

/** Which melt-stage texture a reformable barrier should currently show, from how
 * close its meltsWhen-relevant property sits to the threshold that clears it --
 * stage 0 at rest, the last stage once crossed. Undefined when the object isn't
 * a staged reformable at all. Pure: picking the texture is world logic, applying
 * it to a sprite is not. */
export function reformableTexture(obj: ObjectDef): string | undefined {
  const rule = obj.barrier?.meltsWhen?.[0];
  if (!rule || !obj.barrier?.meltStages?.length) return undefined;
  const value = obj.properties?.[rule.concept] ?? 0;
  const fraction = Math.max(0, Math.min(1, value / rule.threshold));
  const stages = obj.barrier.meltStages;
  const index = Math.min(stages.length - 1, Math.floor(fraction * (stages.length - 1) + 1e-6));
  return stages[index];
}

/** Which texture an AND-gate barrier (2+ meltsWhen rules) should show for its
 * own currently-satisfied rule, index-matched to meltsWhen -- the "one of
 * several requirements met" cue. If more than one is independently satisfied at
 * once, shows the first (arbitrary but stable) rather than trying to combine
 * cues, since every rule clearing together is caught by meltRulesSatisfied and
 * resolved immediately afterward anyway. Undefined when the object isn't a
 * multi-rule barrier with partial textures. */
export function partialMeltTexture(obj: ObjectDef): string | undefined {
  const rules = obj.barrier?.meltsWhen;
  if (!rules || rules.length < 2 || !obj.barrier?.partialMeltTextures?.length) return undefined;
  const satisfied = rules.map((rule) => {
    const value = obj.properties?.[rule.concept] ?? 0;
    return rule.aboveOrBelow === 'above' ? value >= rule.threshold : value <= rule.threshold;
  });
  const soleIndex = satisfied.every(Boolean) ? -1 : satisfied.findIndex(Boolean);
  return soleIndex >= 0 ? (obj.barrier.partialMeltTextures[soleIndex] ?? obj.texture) : obj.texture;
}

/** Drops an object out of the room. The caller's renderer fades and destroys the
 * sprite; the world simply no longer contains it, which is what makes collision
 * drop automatically (isWalkable reads straight from room.objects). */
export function removeObjectFromRoom(room: RoomDef, obj: ObjectDef, out: SimEvents): void {
  room.objects = room.objects.filter((o) => o.id !== obj.id);
  out.push({ kind: 'object-removed', objectId: obj.id });
}

/** Adds a brand-new object to a room at runtime -- Separate·Mass splitting one
 * rock into two, merging two into one, or room10's Rune Circle activating into
 * its Portal. */
export function addObjectToRoom(room: RoomDef, obj: ObjectDef, out: SimEvents): void {
  room.objects.push(obj);
  out.push({ kind: 'object-added', object: obj });
}

// Shared by checkMeltThreshold's own object and its unlocksObjectId/
// unlocksObjectIds follow-through: either removes the object outright (the
// ordinary case every barrier before room9 used), or, if `collapsesInto` is
// set, leaves it in room.objects but permanently flips it walkable and
// swaps its texture to rubble -- room9's bridge pillar and boulders collapse
// in place rather than vanishing, since "the stones fall down, and now you can
// walk over them" reads as debris still being there, not gone -- or, if
// `transformsInto` is set, leaves both room.objects and walkability untouched
// and instead cross-fades to the other texture, for a barrier whose threshold
// represents a property moving elsewhere rather than the object breaking down.
export function resolveBarrier(room: RoomDef, obj: ObjectDef, out: SimEvents): void {
  const barrier = obj.barrier;
  if (barrier?.collapsesInto) {
    // What it was, before this overwrites it -- see BarrierDef.intact.
    //
    // Recorded only for an object with a rule of its *own*, because the rule is
    // what a restore watches: a Warded Boulder carries `collapsesInto` and no
    // `meltsWhen` at all, brought down by the Bridge Pillar naming it in
    // `unlocks`, so there is nothing about the boulder that could ever stop
    // being true. It stays rubble, which is also the honest reading -- the
    // pillar's own Entropy is a thing you can drain back off, and the stones it
    // dropped are not.
    //
    // A chained `secondStage` is excluded for a different reason: restoring
    // would put back a look from a stage the rule has already advanced past,
    // which is a state the object was never in and the threshold no longer
    // describes.
    // **Only on the way down, never again while it is already down.**
    // `checkMeltThreshold` resolves the barrier every time its rules read
    // satisfied, not only on the rising edge -- so anything touching a collapsed
    // object while it is still over threshold (a partial drain, a Project that
    // catches it, marking it as a source for something else) comes back through
    // here. Without this guard the second pass records the *collapsed* look as
    // what to restore to, and the restore puts back rubble. That is a no-op the
    // player reads as "it just does not work", and it is what the first version
    // of this shipped as: the unit test collapsed each object exactly once, and
    // real play never does.
    //
    // `intact === undefined` is precisely "whole, or collapsed by something with
    // no rule of its own", since the restore is what clears it.
    if (barrier.intact === undefined && barrier.meltsWhen?.length && !barrier.secondStage) {
      barrier.intact = { texture: obj.texture, interact: obj.interact, walkable: obj.walkable };
    }
    obj.walkable = true;
    out.push({ kind: 'texture-set', objectId: obj.id, texture: barrier.collapsesInto, transition: 'instant' });
    // Written back, not merely announced: a collapse can be undone and redone
    // (see `checkBarrierRestore`), and the restore's own "has this already been
    // set back" guard compares against this exact field. Sprites are rebuilt
    // from `obj.texture` on every room entry too, so an event alone would
    // forget the collapse the moment the player left and came back.
    obj.texture = barrier.collapsesInto;
    // A collapsed barrier's interaction can meaningfully differ from its
    // whole-and-sealed one (see BarrierDef.collapsedInteract) -- e.g. room6's
    // chest goes from "there's no key for this" to "search the wreck".
    if (barrier.collapsedInteract) obj.interact = barrier.collapsedInteract;
    // A chained secondStage (see BarrierDef.secondStage) advances this
    // object's own meltsWhen/collapsesInto/collapsedInteract in place to
    // the second stage's, right after applying the first's above -- the
    // very next properties mutation against this object is then checked
    // against the new (higher) threshold through this exact same code
    // path, rather than needing a separate staged-check mechanism.
    if (barrier.secondStage) {
      barrier.meltsWhen = barrier.secondStage.meltsWhen;
      barrier.collapsesInto = barrier.secondStage.collapsesInto;
      barrier.collapsedInteract = barrier.secondStage.collapsedInteract;
      barrier.secondStage = undefined;
    }
    // What the player is facing may now read differently (and it just became
    // walkable), so whoever is rendering needs to recompute its faced target --
    // this is the event standing in for the pushFacedTarget() call that used to
    // sit inline here.
    out.push({ kind: 'interact-changed', objectId: obj.id });
  } else if (barrier?.transformsInto) {
    // A genuine toggle, not one-shot: swap to whichever of the object's own
    // base texture or transformsInto it *isn't* currently showing, and flip
    // the rule's own direction so the *next* crossing (from the opposite
    // side) swaps it back. Safe to leave meltsWhen active afterward --
    // nothing in this game passively drifts Entropy, so this only
    // re-triggers when an actual cast touches the object again. Reads/
    // writes the persisted `barrier.transformed` flag rather than any rendered
    // texture -- sprites are destroyed and rebuilt from scratch on every room
    // entry, so the sprite alone isn't a reliable "which state is this in"
    // signal across a revisit (a real bug: room9's water-flow state visually
    // reset to dry every time the room was re-entered, since isWaterPassable
    // used to derive the Aqueduct's restored state that fragile way).
    const isTransformed = !!barrier.transformed;
    barrier.transformed = !isTransformed;
    const nextTexture = isTransformed ? obj.texture : barrier.transformsInto;
    // The other two thirds of the presentation, if the author supplied them
    // (2026-08-11). Recorded on the first transform for the same reason
    // `intact` is recorded on a collapse: the base pair is authored and cannot
    // be recomputed, and the toggle has to be able to put it back. A Ruined
    // Aqueduct that a cast has mended is not still ruined, and until this it
    // said it was -- in its name and in its own description, over a sprite
    // showing the opposite.
    if (barrier.transformedName || barrier.transformedInteract) {
      barrier.untransformed ??= { name: obj.name, interact: obj.interact };
      const shown = barrier.transformed ? { name: barrier.transformedName, interact: barrier.transformedInteract } : barrier.untransformed;
      if (shown.name) obj.name = shown.name;
      if (shown.interact) obj.interact = shown.interact;
      out.push({ kind: 'interact-changed', objectId: obj.id });
    }
    const rule = barrier.meltsWhen?.[0];
    // Just flips which direction the *next* crossing watches -- Transfer
    // already conserves the total between source and target, and this leaves
    // whatever's left in the object's own properties untouched, whichever
    // direction just triggered. Used to zero a 'below' crossing's own value
    // here (2026-07-30, closing a real room6 exploit: the Withered Standard's
    // leftover Entropy, marked as a source a second time, could trip the
    // Iron-Bound Chest's threshold directly, skipping the Reliquary's whole
    // stage-2 role) -- reverted 2026-08-06 in favor of a conservative fix:
    // rebalancing room6's own starting numbers so the same leftover is
    // honestly too small to reach that threshold on its own, rather than
    // discarding real Entropy the total-conservation law says should still
    // be there. See 12_Mechanics_Entropy.md for the room's own numbers.
    if (rule) rule.aboveOrBelow = rule.aboveOrBelow === 'below' ? 'above' : 'below';
    out.push({ kind: 'texture-set', objectId: obj.id, texture: nextTexture, transition: 'cross-fade' });
  } else if (obj.relic) {
    // A relic stays exactly where it is and changes nothing here: its whole
    // presentation is re-derived from its own Entropy by the `relic-decay`
    // settle system (sim/relic.ts), which runs after this and does not care
    // what crossed a threshold or whether anything did. So this branch exists
    // purely to *not* fall through to the removal below -- the barrier on a
    // relic is only ever the puzzle-trigger half (`meltsWhen` + `unlocks`),
    // never its appearance.
    //
    // This is what `transformsInto` used to be for on these objects, and the
    // difference is the point: a toggle had to own the texture, so it had to
    // flip its own rule to avoid re-firing, so the rule could never keep
    // watching one direction -- and the name, description and signature it did
    // not own drifted out of sync with the sprite it did. Deriving the whole
    // presentation live means the barrier can go back to meaning one thing.
  } else {
    removeObjectFromRoom(room, obj, out);
  }
}

/** The exact inverse of `resolveBarrier`'s collapse: puts back the look, the
 * interaction and the solidity an object had before its own rule crossed, once
 * that rule stops being satisfied.
 *
 * Reversible because the quantity is. Entropy is not consumed by crossing a
 * threshold -- it is moved, and an ordinary Transfer moves it back -- so an
 * Iron-Bound Chest that shook itself apart at Entropy 4 is whole again once it
 * is drained below 4, the same way rust, a relic's three stages, a vine's Dead
 * stage and a melted candle all already reverse. Collapse was the last
 * threshold mechanism in the game that only ran one way, which made it read as
 * a rule of the world ("breaking is permanent") when it was really an
 * unfinished mechanism.
 *
 * What does *not* come back is anything the collapse did elsewhere: a key
 * granted, a ward opened, a Boulder brought down by `unlocks`. Those happened,
 * and this only ever restores the object's own state -- the same line
 * `checkCandleRestore` already draws.
 *
 * `barrier.intact` is both the memory and the "is it collapsed" flag: recorded
 * on the way down, cleared on the way back up, so this is a no-op for an object
 * that is whole and for one that was never restorable in the first place. */
export function checkBarrierRestore(obj: ObjectDef, out: SimEvents): void {
  const intact = obj.barrier?.intact;
  if (!intact || meltRulesSatisfied(obj)) return;
  obj.barrier!.intact = undefined;
  obj.walkable = intact.walkable;
  if (obj.texture !== intact.texture) {
    // Written back, not merely announced -- sprites are rebuilt from
    // `obj.texture` on every room entry, so an event alone would forget the
    // restore the moment the player left and came back. Same reason
    // `resolveBarrier`, `refreshRustTexture` and `checkCandleRestore` all write
    // the field as well as emitting.
    obj.texture = intact.texture;
    out.push({ kind: 'texture-set', objectId: obj.id, texture: intact.texture, transition: 'instant' });
  }
  obj.interact = intact.interact;
  out.push({ kind: 'interact-changed', objectId: obj.id });
}

/** The declared system's own body (see settle.ts's `barrier-restore`): every
 * collapsed-but-restorable object in the room, re-checked every settle.
 *
 * A settle system rather than another `checkMeltThreshold` branch, for the same
 * reason `candle-restore` is one: a Bind·Entropy partner can drain the object
 * with no cast ever touching it, so the restore has to be something the world
 * notices rather than something a cast reports. */
export function settleBarrierRestores(room: RoomDef, out: SimEvents): void {
  for (const obj of room.objects) checkBarrierRestore(obj, out);
}

// Checks an object's meltsWhen threshold and, if crossed, either removes it
// permanently (and, per unlocksObjectId, a second linked object too) or, if
// `reformable`, leaves it in place -- collision for the permanent case drops
// automatically since isWalkable() reads straight from room.objects, and for
// the reformable case isWalkable() re-checks meltRulesSatisfied itself on
// every call instead of relying on a one-time removal.
//
// `flowingTiles` (default undefined, read as "assume every water tile has
// water" -- the safe default for the many callers that never touch a water
// tile at all) is `WorldState.flowingTiles` as of the *previous* settle --
// the caller's own, already-computed answer to "which tiles currently carry
// water," passed through untouched rather than re-traced here. Only ever
// consulted for a water tile attempting to freeze (see checkWaterFreeze's
// own doc comment on why thawing never needs it).
export function checkMeltThreshold(room: RoomDef, obj: ObjectDef, out: SimEvents, physics: PhysicsConfig = DEFAULT_PHYSICS, flowingTiles?: Set<string>): boolean {
  // Independent of everything below -- a HeatStateDef ladder has nothing to
  // do with BarrierDef.meltsWhen, but reusing this function's own call sites
  // (every cast that touches a real object, plus applyRoomDecay's inline
  // call) is exactly what lets no cast handler need to know heatState exists
  // at all. See HeatStateDef's own doc comment (types.ts) for why this can't
  // just be a declared settle system instead.
  if (obj.heatState) checkHeatStateTransition(obj, out);
  // Same reasoning, a second mechanism reusing the same free ride: an
  // ordinary water channel tile's own freeze/thaw (see checkWaterFreeze,
  // heatState.ts) -- deliberately not a heatState object itself, since water
  // never decays toward ambient the way a heat source does. Needs `physics`
  // (the freeze/melt thresholds live in PhysicsConfig.water, not per-object
  // JSON), which is why this function takes one now.
  if (obj.water?.flowingTexture || obj.water?.body) {
    // A standing body always holds its fluid; a channel holds it only while a
    // trace reaches it. `undefined` still reads as "assume it does", for the
    // many callers that never touch a fluid tile at all.
    //
    // A bridge reaching here is filtered out by `checkWaterFreeze` itself
    // rather than by this guard: room9's Aqueduct carries a `flowingTexture`
    // as of 2026-08-11 (so the flow pass can draw it wet only while water is
    // actually crossing it), and a structure that carries a fluid is still not
    // a body of one. Stating the rule in one place beats two guards that have
    // to agree.
    const hasFluid = flowingTiles === undefined || !!obj.water.body || flowingTiles.has(`${obj.x},${obj.y}`);
    checkWaterFreeze(obj, physics, out, hasFluid);
  }

  // Refreshed on every call regardless of whether the threshold is crossed --
  // this runs after every properties mutation (a cast, a decay tick), so it's
  // the one place that keeps a reformable object's melt-stage visual in sync
  // with its current value either direction, not just at the moment it flips.
  if (obj.barrier?.reformable) {
    const texture = reformableTexture(obj);
    if (texture) out.push({ kind: 'texture-set', objectId: obj.id, texture, transition: 'instant' });
  } else if (obj.barrier?.partialMeltTextures?.length) {
    const texture = partialMeltTexture(obj);
    if (texture) out.push({ kind: 'texture-set', objectId: obj.id, texture, transition: 'instant' });
  }

  if (!meltRulesSatisfied(obj)) {
    // Re-arm a relic's trigger the moment its rules stop holding, which is what
    // makes this a rising edge rather than a one-shot latch -- see
    // BarrierDef.resolved. Nothing else needs it: every other barrier either
    // leaves the room, flips its own rule, or is `reformable` and returns above.
    if (obj.relic && obj.barrier) obj.barrier.resolved = false;
    return false;
  }
  if (obj.barrier?.reformable) return true;
  // A relic's rules keep reading satisfied for as long as its Entropy stays
  // across the threshold, because nothing about crossing one changes what it is
  // watching. Fire the trigger side (puzzle-solved, and whatever `unlocks`
  // names) exactly once per crossing.
  if (obj.relic && obj.barrier) {
    if (obj.barrier.resolved) return false;
    obj.barrier.resolved = true;
  }

  out.push({ kind: 'puzzle-solved', objectId: obj.id });
  const hadSecondStage = !!obj.barrier?.secondStage;
  resolveBarrier(room, obj, out);
  for (const id of obj.barrier?.unlocks ?? []) {
    const linked = room.objects.find((o) => o.id === id);
    if (linked) resolveBarrier(room, linked, out);
  }
  // If this call just consumed a chained secondStage, the same properties
  // value that crossed the first (lower) threshold might already clear the
  // new, higher one too -- a single cast big enough for both at once --
  // recheck immediately against the now-advanced meltsWhen rather than
  // leaving it one cast behind. Gated on hadSecondStage specifically so
  // this never re-fires resolveBarrier on an ordinary single-stage object,
  // which would wrongly double-toggle a `transformsInto` object (it flips
  // direction on every call) or just redundantly repeat everything else.
  if (hadSecondStage && meltRulesSatisfied(obj)) resolveBarrier(room, obj, out);
  return true;
}
